
import Decimal from 'decimal.js';
import * as Enumerable from 'linq';

export enum RoundingUnit {
    Second = 's',
    Minute = 'm',
    Hour = 'h',
    Day = 'd',
    Week = 'w',
    Year = 'y',
    Millisecond = 'ms',
    Microsecond = 'µs',
    Nanosecond = 'ns'
}

export const RoundingUnits = Object.values(RoundingUnit);

const s = 1;
const m = s * 60;
const h = m * 60;
const d = h * 24;
const w = d * 7;
const y = d * 365.25;
const ms = s / 1000;
const us = ms / 1000;
const ns = us / 1000;

const timeUnitsList = [
    { unit: RoundingUnit.Year, amount: y },
    { unit: RoundingUnit.Week, amount: w },
    { unit: RoundingUnit.Day, amount: d },
    { unit: RoundingUnit.Hour, amount: h },
    { unit: RoundingUnit.Minute, amount: m },
    { unit: RoundingUnit.Second, amount: s },
    { unit: RoundingUnit.Millisecond, amount: ms },
    { unit: RoundingUnit.Microsecond, amount: us },
    { unit: RoundingUnit.Nanosecond, amount: ns }
];

const timeUnits = Enumerable
    .from(timeUnitsList)
    .toObject(x => x.unit, x => x.amount) as { [key: string]: number };


export class DurationUtility {

    /**
     * Formats a number of seconds.
     * @param {number} seconds The number to stringify.
     * @param {string} rounding The unit used for rounding. Defaults to 'ms'.
     * @return {string} The formatted and normalized string.
     */
    public static stringify(seconds: number, rounding = RoundingUnit.Hour) {

        if (!seconds)
            seconds = 0;

        let str = seconds < 0
            ? '-'
            : '';

        let nbr = Math.abs(seconds);
        let shouldStop = false;

        Enumerable
            .from(timeUnitsList)
            .forEach((el) => {
                if (shouldStop) {
                    return
                }

                if (el.unit === rounding) {
                    shouldStop = true
                }

                const tmp = el.unit === rounding ? new Decimal(nbr / el?.amount).toDecimalPlaces(2).toNumber() : Math.floor(nbr / el.amount);
                nbr -= tmp * el.amount;

                if (tmp !== 0) {
                    str += `${tmp}${el.unit} `
                }

            });
        return str.length > 0 ? str.trim() : `0${rounding}`
    }


    /**
     * Parses a string using the Simple Duration Format and returns the number of seconds
     * corresponding to it.
     * @param {string} str The string to parse
     * @return {number} The number of seconds
     */
    public static parse(str: string): number {

        if (!str || !Enumerable.from(RoundingUnits).any(x => str.indexOf(x) != -1)) {
            if (!str) {
                str = `0${RoundingUnit.Hour}`;
            } else {
                str += RoundingUnit.Hour;
            }
        }

        const wsRegex = '[ \\t]'
        // Accept leading zeros (e.g. "099999") instead of rejecting them: a strict
        // no-leading-zero rule made the input silently coerce to 0, letting invalid
        // deductible values slip past the "deductible <= duration" check (WBS-6441).
        // Accept a comma as the decimal separator ("4,5h") equivalently to a dot so
        // comma-locale input persists instead of failing to parse (WBS-9720).
        const positiveNumberRegex = '(?:\\d+)(?:[.,]\\d+)?'
        const unitRegex = '(?:ns|µs|ms|s|m|h|d|w|y)'
        const elementRegex = `(?:(${positiveNumberRegex})${wsRegex}*(${unitRegex}))`
        const wholeRegex = `^${wsRegex}*(-)?((?:${wsRegex}*${elementRegex}${wsRegex}*)+)$`

        const element = new RegExp(elementRegex)
        const whole = new RegExp(wholeRegex)

        const match = str.match(whole);

        if (!match) {
            throw new Error(`${str} is not a valid Duration Format`);
        }

        const negative = match[1] === '-';
        const elements = match[2];

        const re = new RegExp(element.source, 'g');
        let nmatch;
        let counter = 0;

        while ((nmatch = re.exec(elements)) !== null) {

            const nbr = parseFloat(nmatch[1].replace(',', '.'))
            const unit = nmatch[2]
            counter += nbr * timeUnits[unit];
        }

        return (negative ? -1 : 1) * counter
    }

    public static formatDurationForFullCalendar(durationInSeconds: number): string {
        const hours = Math.floor(durationInSeconds / 3600);
        const minutes = Math.floor((durationInSeconds % 3600) / 60);

        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = minutes.toString().padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}`;
    }

}

